import { Rng } from './rng.js';
import { Track, TrackDef, TrackFeature, EnvironmentId, MedalTimes } from './track.js';
import { SplinePoint, makeFrame } from './spline.js';

// ── validation ──────────────────────────────────────────────────────────────

export interface TrackValidation {
  ok: boolean;
  problems: string[];
  minTurnRadius: number;
  length: number;
}

/** Static sanity checks that every track (authored or generated) must pass. */
export function validateTrack(track: Track): TrackValidation {
  const problems: string[] = [];
  const sp = track.main;
  let minRadius = Infinity;

  // curvature: no corner tighter than ~18m radius (unflyable at racing speed)
  for (let i = 0; i < sp.count; i++) {
    const k = sp.curvatureAt(i * sp.spacing);
    if (k > 1e-6) minRadius = Math.min(minRadius, 1 / k);
  }
  if (minRadius < 17) problems.push(`corner too tight: radius ${minRadius.toFixed(1)}m`);

  // self-intersection: non-adjacent samples must keep clearance unless vertically separated
  const cell = 40;
  const grid = new Map<string, number[]>();
  for (let i = 0; i < sp.count; i++) {
    const key = `${Math.floor(sp.pos[i * 3] / cell)},${Math.floor(sp.pos[i * 3 + 2] / cell)}`;
    let arr = grid.get(key);
    if (!arr) grid.set(key, (arr = []));
    arr.push(i);
  }
  const minSep = 2 + Math.max(...Array.from({ length: sp.count }, (_, i) => sp.width[i])) * 0.9;
  outer: for (let i = 0; i < sp.count; i++) {
    const cx = Math.floor(sp.pos[i * 3] / cell);
    const cz = Math.floor(sp.pos[i * 3 + 2] / cell);
    for (let ox = -1; ox <= 1; ox++)
      for (let oz = -1; oz <= 1; oz++) {
        const arr = grid.get(`${cx + ox},${cz + oz}`);
        if (!arr) continue;
        for (const j of arr) {
          let gap = Math.abs(i - j);
          if (sp.closed) gap = Math.min(gap, sp.count - gap);
          if (gap * sp.spacing < 60) continue; // adjacent along the ribbon
          const dx = sp.pos[i * 3] - sp.pos[j * 3];
          const dy = sp.pos[i * 3 + 1] - sp.pos[j * 3 + 1];
          const dz = sp.pos[i * 3 + 2] - sp.pos[j * 3 + 2];
          const dHoriz = Math.sqrt(dx * dx + dz * dz);
          if (dHoriz < minSep && Math.abs(dy) < 14) {
            problems.push(
              `self-intersection near s=${(i * sp.spacing).toFixed(0)}m / s=${(j * sp.spacing).toFixed(0)}m`
            );
            break outer;
          }
        }
      }
  }

  // jump zones must be crossable: gap length within ballistic reach at plan speed
  for (const z of track.jumpZones) {
    const li = Math.floor(z.sStart / sp.spacing) % track.racingLine.speed.length;
    const v = track.racingLine.speed[li];
    const gapLen = z.sEnd - z.sStart;
    const maxReach = v * 2.2; // ~2.2s of committed flight
    if (gapLen > maxReach) problems.push(`jump at s=${z.sStart.toFixed(0)} too long (${gapLen.toFixed(0)}m)`);
  }

  if (track.length < 900) problems.push(`track too short (${track.length.toFixed(0)}m)`);
  if (track.length > 14000) problems.push(`track too long (${track.length.toFixed(0)}m)`);

  return { ok: problems.length === 0, problems, minTurnRadius: minRadius, length: track.length };
}

/** Estimate a competent lap time by integrating the racing-line speed plan. */
export function estimateLapSeconds(track: Track): number {
  const sp = track.main;
  const line = track.racingLine;
  let t = 0;
  for (let i = 0; i < sp.count; i++) {
    t += sp.spacing / Math.max(18, line.speed[i] * 0.88);
  }
  return t;
}

export function estimateMedals(track: Track): MedalTimes {
  const base = estimateLapSeconds(track);
  return {
    author: round1(base * 0.93),
    gold: round1(base * 1.0),
    silver: round1(base * 1.12),
    bronze: round1(base * 1.3),
  };
}

function round1(v: number): number {
  return Math.round(v * 10) / 10;
}

// ── procedural generation ───────────────────────────────────────────────────

const PROC_ENVIRONMENTS: EnvironmentId[] = [
  'neonhaven',
  'reactor',
  'duneworks',
  'stormline',
  'shipyard',
  'aurora',
  'voidgate',
];

/**
 * Deterministic seeded circuit generator. Same seed → same track everywhere,
 * so seeds are shareable and daily challenges are fair.
 */
export function generateTrack(seed: string): TrackDef {
  // bounded retry: perturb sub-seed until validation passes
  for (let attempt = 0; attempt < 24; attempt++) {
    const def = generateAttempt(seed, attempt);
    const track = new Track(def);
    const v = validateTrack(track);
    if (v.ok) {
      def.medals = estimateMedals(track);
      return def;
    }
  }
  // last resort: a known-good simple oval so the game never breaks on a bad seed
  return fallbackOval(seed);
}

function generateAttempt(seed: string, attempt: number): TrackDef {
  const rng = new Rng(`${seed}::${attempt}`);
  const env = PROC_ENVIRONMENTS[rng.int(0, PROC_ENVIRONMENTS.length - 1)];

  const pointCount = rng.int(10, 15);
  const baseRadius = rng.range(320, 620);
  const width = rng.range(22, 30);
  const heightAmp = rng.range(8, 55);
  const radiusJitter = rng.range(0.18, 0.42);

  const points: SplinePoint[] = [];
  // smooth radial noise via a few random harmonics (deterministic)
  const harmonics = [
    { f: 2, a: rng.range(0.2, 1), p: rng.range(0, Math.PI * 2) },
    { f: 3, a: rng.range(0.2, 0.8), p: rng.range(0, Math.PI * 2) },
    { f: 5, a: rng.range(0.1, 0.5), p: rng.range(0, Math.PI * 2) },
  ];
  const hHarmonics = [
    { f: 1, a: rng.range(0.2, 1), p: rng.range(0, Math.PI * 2) },
    { f: 2, a: rng.range(0.3, 1), p: rng.range(0, Math.PI * 2) },
    { f: 4, a: rng.range(0.1, 0.6), p: rng.range(0, Math.PI * 2) },
  ];

  for (let i = 0; i < pointCount; i++) {
    const ang = (i / pointCount) * Math.PI * 2 + rng.range(-0.12, 0.12);
    let radial = 0;
    for (const h of harmonics) radial += Math.sin(ang * h.f + h.p) * h.a;
    const r = baseRadius * (1 + radial * radiusJitter * 0.5);
    let hgt = 0;
    for (const h of hHarmonics) hgt += Math.sin(ang * h.f + h.p) * h.a;
    points.push({
      x: Math.cos(ang) * r,
      y: 40 + hgt * heightAmp,
      z: Math.sin(ang) * r,
      width: width * rng.range(0.85, 1.25),
      roll: 0,
    });
  }

  // banking: roll into turns based on local direction change
  for (let i = 0; i < pointCount; i++) {
    const prev = points[(i - 1 + pointCount) % pointCount];
    const next = points[(i + 1) % pointCount];
    const v1x = points[i].x - prev.x, v1z = points[i].z - prev.z;
    const v2x = next.x - points[i].x, v2z = next.z - points[i].z;
    const crossY = v1x * v2z - v1z * v2x;
    const l1 = Math.hypot(v1x, v1z), l2 = Math.hypot(v2x, v2z);
    if (l1 > 1 && l2 > 1) {
      const turn = crossY / (l1 * l2);
      points[i].roll = -turn * rng.range(0.35, 0.8);
    }
  }

  // features
  const features: TrackFeature[] = [];
  const padCount = rng.int(3, 6);
  for (let i = 0; i < padCount; i++) {
    features.push({
      kind: 'boost',
      s: rng.range(0.05, 0.95),
      length: 0.006,
      lane: rng.range(-0.5, 0.5),
    });
  }
  if (rng.chance(0.55)) {
    features.push({ kind: 'jump', s: rng.range(0.3, 0.8), length: rng.range(0.008, 0.014) });
  }
  if (rng.chance(0.5)) {
    features.push({
      kind: 'hazard',
      s: rng.range(0.15, 0.85),
      length: 0.01,
      lane: rng.range(-0.4, 0.4),
      width: rng.range(4, 7),
      type: rng.chance(0.5) ? 'barrier' : 'slick',
    });
  }

  return {
    id: `seed:${seed}`,
    name: seedName(seed),
    tagline: 'Uncharted circuit — generated from a shared seed.',
    environment: env,
    difficulty: (rng.int(1, 4) as 1 | 2 | 3 | 4),
    layout: 'circuit',
    defaultLaps: 3,
    width,
    points,
    features,
    unlockLevel: 0,
    medals: { bronze: 120, silver: 105, gold: 95, author: 90 }, // replaced by estimate after validation
    seed,
  };
}

function fallbackOval(seed: string): TrackDef {
  const points: SplinePoint[] = [];
  const n = 12;
  for (let i = 0; i < n; i++) {
    const ang = (i / n) * Math.PI * 2;
    points.push({ x: Math.cos(ang) * 420, y: 40 + Math.sin(ang * 2) * 8, z: Math.sin(ang) * 300, width: 26 });
  }
  const def: TrackDef = {
    id: `seed:${seed}`,
    name: seedName(seed),
    tagline: 'Uncharted circuit — generated from a shared seed.',
    environment: 'voidgate',
    difficulty: 1,
    layout: 'circuit',
    defaultLaps: 3,
    width: 26,
    points,
    features: [
      { kind: 'boost', s: 0.2, length: 0.008, lane: 0 },
      { kind: 'boost', s: 0.7, length: 0.008, lane: 0 },
    ],
    unlockLevel: 0,
    medals: { bronze: 120, silver: 105, gold: 95, author: 90 },
    seed,
  };
  const track = new Track(def);
  def.medals = estimateMedals(track);
  return def;
}

/** Deterministic evocative name from a seed. */
function seedName(seed: string): string {
  const rng = new Rng(`name::${seed}`);
  const a = ['IRON', 'NULL', 'EMBER', 'VECTOR', 'HALO', 'STATIC', 'CRYO', 'DELTA', 'ZERO', 'FLUX'];
  const b = ['CIRCUIT', 'RUN', 'SPIRE', 'CHANNEL', 'GATE', 'CRADLE', 'VERGE', 'SPAN', 'HELIX', 'REACH'];
  return `${rng.pick(a)} ${rng.pick(b)}`;
}

// small util used by the client to know a frame's world-space start pose for previews
export function trackPreviewPoints(track: Track, count = 220): Float32Array {
  const out = new Float32Array(count * 3);
  const f = makeFrame();
  for (let i = 0; i < count; i++) {
    track.main.frameAt((i / count) * track.length, f);
    out[i * 3] = f.pos.x;
    out[i * 3 + 1] = f.pos.y;
    out[i * 3 + 2] = f.pos.z;
  }
  return out;
}

export const _internal = { generateAttempt, seedName };
